import { FeatureFlagService, FeatureFlagValues } from "./featureFlagService";
import {
  POSTHOG_FEATURE_FLAG_DEFAULTS,
  POSTHOG_FEATURE_FLAGS,
  PostHogFeatureFlagKey,
  isPosthogFeatureFlagKey,
} from "./posthogFeatureFlagDefinitions";

type FeatureFlagEvaluator = (flagKey: string) => boolean | undefined;

interface HandlePosthogPayloadOptions {
  flags?: string[];
  errorsLoading: boolean;
  evaluator: FeatureFlagEvaluator;
}

const STORAGE_KEY = "fh_plus_posthog_feature_flags";
const STORAGE_VERSION = 1;

class PostHogFeatureFlagService extends FeatureFlagService<PostHogFeatureFlagKey> {
  constructor() {
    super({
      storageKey: STORAGE_KEY,
      storageVersion: STORAGE_VERSION,
      flagKeys: Object.values(POSTHOG_FEATURE_FLAGS),
      defaultValues: POSTHOG_FEATURE_FLAG_DEFAULTS,
      logLabel: "PostHogFlags",
    });
  }

  handlePosthogPayload({
    flags,
    errorsLoading,
    evaluator,
  }: HandlePosthogPayloadOptions): void {
    if (errorsLoading) {
      this.applyFallbackDefaults("PostHog reported errors loading flags", {
        flagsCount: flags?.length ?? 0,
      });
      return;
    }

    const recognizedFlags =
      Array.isArray(flags) && flags.length
        ? flags.filter(isPosthogFeatureFlagKey)
        : [];

    if (recognizedFlags.length === 0 && !this.hasResolvedValues) {
      this.applyFallbackDefaults(
        "PostHog returned no recognized feature flags; using defaults",
        { flagsCount: flags?.length ?? 0 }
      );
      return;
    }

    const evaluatedFlags = this.evaluateFlags(evaluator);
    this.persistValues(evaluatedFlags);
  }

  private evaluateFlags(
    evaluator: FeatureFlagEvaluator
  ): Partial<FeatureFlagValues<PostHogFeatureFlagKey>> {
    const results: Partial<FeatureFlagValues<PostHogFeatureFlagKey>> = {};

    Object.values(POSTHOG_FEATURE_FLAGS).forEach((flagKey) => {
      try {
        const evaluation = evaluator(flagKey);
        results[flagKey] =
          typeof evaluation === "boolean"
            ? evaluation
            : POSTHOG_FEATURE_FLAG_DEFAULTS[flagKey];
      } catch (error) {
        results[flagKey] = POSTHOG_FEATURE_FLAG_DEFAULTS[flagKey];
        this.logError(`Failed to evaluate PostHog flag: ${flagKey}`, error);
      }
    });

    return results;
  }
}

export const posthogFeatureFlagService = new PostHogFeatureFlagService();
